import { useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, Zap } from "lucide-react";

const ActivePlanTab = ({ subscription }) => {
  const navigate = useNavigate();

  // Status configuration
  const statusConfig = {
    active: {
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      label: "Active",
    },
    past_due: {
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      label: "Past Due",
    },
    canceled: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      label: "Canceled",
    },
    trialing: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      label: "Trial",
    },
    incomplete: {
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      label: "Incomplete",
    },
  };

  if (!subscription) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-teal-600" />
        </div>

        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          No Active Subscription
        </h3>

        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Unlock premium features and maximize your productivity with our
          tailored plans.
        </p>

        <div className="space-y-3 max-w-sm mx-auto">
          <button
            onClick={() => navigate("/subscription-plans")}
            className="w-full px-6 py-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Explore Plans
          </button>
        </div>
      </div>
    );
  }

  const calculateDays = () => {
    const startDate = new Date(subscription.current_period_start);
    const endDate = new Date(subscription.current_period_end);
    const now = new Date();

    // Normalize times (important)
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    const MS_PER_DAY = 1000 * 60 * 60 * 24;

    const totalDays = Math.max(
      Math.round((endDate - startDate) / MS_PER_DAY),
      1,
    );

    const elapsedDays = Math.min(
      Math.max(Math.round((now - startDate) / MS_PER_DAY), 0),
      totalDays,
    );

    const daysLeft = Math.max(totalDays - elapsedDays, 0);

    const percentageUsed = Math.round((elapsedDays / totalDays) * 100);
    const percentageRemaining = 100 - percentageUsed;

    return {
      totalDays,
      elapsedDays,
      daysLeft,
      percentageUsed,
      percentageRemaining,
    };
  };

  const getRemainingTimeText = () => {
    const now = new Date();
    const endDate = new Date(subscription.current_period_end);

    const diffMs = endDate - now;

    if (diffMs <= 0) {
      return "Subscription expired!";
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays >= 1) {
      return diffDays === 1
        ? "Subscription expires in 1 day"
        : `Subscription expires in ${diffDays} days`;
    }

    if (diffHours >= 1) {
      return diffHours === 1
        ? "Subscription expires in 1 hour"
        : `Subscription expires in ${diffHours} hours`;
    }

    return diffMinutes <= 1
      ? "Subscription expires in 1 minute"
      : `Subscription expires in ${diffMinutes} minutes`;
  };

  const days = calculateDays();
  const status = subscription.status?.toLowerCase() || "active";
  const config = statusConfig[status] || statusConfig.active;

  const getDaysLeftColor = () => {
    if (days.daysLeft > 30) return "text-emerald-600";
    if (days.daysLeft > 7) return "text-amber-600";
    return "text-red-600";
  };

  const getProgressColor = () => {
    if (days.daysLeft > 30)
      return "bg-gradient-to-r from-emerald-500 to-teal-500";
    if (days.daysLeft > 7)
      return "bg-gradient-to-r from-amber-500 to-orange-500";
    return "bg-gradient-to-r from-red-500 to-pink-500";
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-lg border border-gray-100">
      {/* Header with Status Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {subscription.title}
          </h2>
        </div>

        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${config.bgColor} ${config.color}`}
        >
          <span className="font-semibold">{config.label}</span>
        </div>
      </div>

      {/* Days Progress Bar - Used on RIGHT, Remaining on LEFT */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${getProgressColor().split(" ")[0]}`}
            ></div>
            <span className="font-medium">{days.daysLeft} days left</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-gray-300 to-gray-400"></div>
            <span className="font-medium">{days.elapsedDays} days used</span>
          </div>
        </div>

        {/* Progress Bar Container - Left to Right: Remaining → Used */}
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
          {/* Remaining Days (LEFT side) */}
          <div
            className={`h-full ${getProgressColor()}`}
            style={{ width: `${days.percentageRemaining}%` }}
            title={`${days.daysLeft} days left`}
          />

          {/* Used Days (RIGHT side) */}
          <div
            className="h-full bg-gradient-to-r from-gray-300 to-gray-400"
            style={{ width: `${days.percentageUsed}%` }}
            title={`${days.elapsedDays} days used`}
          />
        </div>
      </div>

      {/* Plan Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="font-medium text-gray-800">
                {new Date(subscription.current_period_start).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Expiry Date</p>
              <p className="font-medium text-gray-800">
                {new Date(subscription.current_period_end).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Days Summary Card */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
          <p className="text-sm text-gray-600 font-medium">Total Days</p>
          <p className="text-2xl font-bold text-gray-800">{days.totalDays}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200">
          <p className="text-sm text-emerald-600 font-medium">Days Used</p>
          <p className="text-2xl font-bold text-gray-800">{days.elapsedDays}</p>
        </div>
        <div
          className={`bg-gradient-to-br ${getDaysLeftColor().replace("text-", "").split(" ")[0] === "text-emerald-600" ? "from-emerald-50 to-teal-50 border-emerald-200" : getDaysLeftColor().replace("text-", "").split(" ")[0] === "text-amber-600" ? "from-amber-50 to-orange-50 border-amber-200" : "from-red-50 to-pink-50 border-red-200"} p-4 rounded-xl border`}
        >
          <p className={`text-sm ${getDaysLeftColor()} font-medium`}>
            Days Left
          </p>
          <p className={`text-2xl font-bold ${getDaysLeftColor()}`}>
            {days.daysLeft}
          </p>
        </div>
      </div>

      {/* Warning Messages */}
      {days.daysLeft <= 7 && (
        <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">{getRemainingTimeText()}</span>
          </div>
          <p className="text-amber-600 text-sm">
            Renew now to continue enjoying all premium features without
            interruption.
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivePlanTab;
